/**
 * Quick add: full product page fetch + parse [data-product-grid-content] (no Section Rendering).
 * Immediate modal + skeleton/shimmer; per-trigger busy + aria; inline error + retry.
 * Hover/click requests for the same product URL share a single in-flight fetch.
 */
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** @type {string | null} */
let quickAddModalFetchUrl = null;
let quickAddModalRequestGeneration = 0;

/** @type {HTMLButtonElement | null} */
let quickAddBusyTrigger = null;
/** @type {Map<string, { controller: AbortController, promise: Promise<Document> }>} */
const quickAddPageInflight = new Map();

/**
 * @param {string} productPageUrl
 * @returns {string}
 */
function quickAddCacheKey(productPageUrl) {
  if (!productPageUrl) return '';
  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.delete('section_id');
  return url.toString();
}

/**
 * @param {string} productPageUrl
 */
function abortQuickAddInflightByUrl(productPageUrl) {
  const key = quickAddCacheKey(productPageUrl);
  if (!key) return;
  quickAddPageInflight.get(key)?.controller.abort();
}

/**
 * @param {string} productPageUrl
 */
function clearQuickAddModalFetchUrl(productPageUrl) {
  if (quickAddModalFetchUrl === productPageUrl) {
    quickAddModalFetchUrl = null;
  }
}

function abortQuickAddModalFetch() {
  if (!quickAddModalFetchUrl) return;
  abortQuickAddInflightByUrl(quickAddModalFetchUrl);
  quickAddModalFetchUrl = null;
}

/**
 * @param {string} productPageUrl
 * @returns {Promise<Document>}
 */
function loadQuickAddPageDocument(productPageUrl) {
  const requestUrl = quickAddCacheKey(productPageUrl);
  if (!requestUrl) {
    throw new Error('Missing product page URL');
  }

  const existing = quickAddPageInflight.get(requestUrl);
  if (existing) {
    return existing.promise;
  }

  const controller = new AbortController();
  const promise = fetch(requestUrl, { signal: controller.signal })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      return response.text();
    })
    .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'))
    .finally(() => {
      quickAddPageInflight.delete(requestUrl);
    });

  quickAddPageInflight.set(requestUrl, { controller, promise });
  return promise;
}

/**
 * Aborts in-flight modal fetch and clears tile busy state. Call when the dialog closes.
 */
export function resetQuickAddLoadingUi() {
  abortQuickAddModalFetch();
  quickAddModalRequestGeneration += 1;
  clearQuickAddTriggerBusy();
}

function clearQuickAddTriggerBusy() {
  if (quickAddBusyTrigger) {
    const prev = quickAddBusyTrigger.dataset.quickAddPreviousAriaLabel;
    if (prev !== undefined) {
      if (prev === '') {
        quickAddBusyTrigger.removeAttribute('aria-label');
      } else {
        quickAddBusyTrigger.setAttribute('aria-label', prev);
      }
      delete quickAddBusyTrigger.dataset.quickAddPreviousAriaLabel;
    }
    quickAddBusyTrigger.classList.remove('quick-add__button--busy');
    quickAddBusyTrigger.removeAttribute('aria-busy');
    quickAddBusyTrigger.disabled = false;
    quickAddBusyTrigger = null;
  }
}

/**
 * @param {HTMLButtonElement} trigger
 * @param {string} [loadingAnnouncement]
 */
function setQuickAddTriggerBusy(trigger, loadingAnnouncement) {
  clearQuickAddTriggerBusy();
  if (!(trigger instanceof HTMLButtonElement)) return;
  quickAddBusyTrigger = trigger;
  if (trigger.dataset.quickAddPreviousAriaLabel === undefined) {
    trigger.dataset.quickAddPreviousAriaLabel = trigger.getAttribute('aria-label') ?? '';
  }
  if (loadingAnnouncement) {
    trigger.setAttribute('aria-label', loadingAnnouncement);
  }
  trigger.classList.add('quick-add__button--busy');
  trigger.setAttribute('aria-busy', 'true');
  trigger.disabled = true;
}

export class QuickAddComponent extends Component {
  /** @type {string | null} */
  #hoverPrefetchUrl = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {HTMLButtonElement | null} */
  #lastTrigger = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    if (this.#hoverPrefetchUrl) {
      abortQuickAddInflightByUrl(this.#hoverPrefetchUrl);
      this.#hoverPrefetchUrl = null;
    }
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    let trigger = event.currentTarget;
    if (!(trigger instanceof HTMLButtonElement)) {
      trigger = this.querySelector('.quick-add__button--choose');
    }
    if (!(trigger instanceof HTMLButtonElement)) return;
    this.#lastTrigger = trigger;
    await this.#runQuickAddFlow(trigger);
  };

  /**
   * Retries load after an error (modal retry control).
   */
  retryQuickAdd = async () => {
    if (!this.#lastTrigger) return;
    await this.#runQuickAddFlow(this.#lastTrigger);
  };

  /**
   * @param {HTMLButtonElement} trigger
   */
  #runQuickAddFlow = async (trigger) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productPageUrl = this.productPageUrl;
    const cacheKey = quickAddCacheKey(productPageUrl);
    if (!cacheKey) return;

    abortQuickAddModalFetch();
    const requestId = ++quickAddModalRequestGeneration;
    quickAddModalFetchUrl = productPageUrl;

    const loadingAnnouncement = this.dataset.loadingAnnouncement?.trim();
    setQuickAddTriggerBusy(trigger, loadingAnnouncement);
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const dialogWasOpen = Boolean(dialogComponent.refs?.dialog?.open);
    let productGrid = this.#cachedContent.get(cacheKey);

    if (productGrid) {
      if (!dialogWasOpen) {
        this.#openQuickAddModal(dialogComponent);
      }
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      if (dialogWasOpen) {
        this.#resetScroll();
      }
      clearQuickAddTriggerBusy();
      clearQuickAddModalFetchUrl(productPageUrl);
      return;
    }

    if (!dialogWasOpen) {
      this.#showModalLoading();
      this.#openQuickAddModal(dialogComponent);
      this.#focusModalLoadingRegion();
    } else {
      this.#showModalLoading();
      this.#focusModalLoadingRegion();
    }

    let html = null;
    try {
      html = await this.#fetchProductPageDocument(productPageUrl);
    } catch {
      if (requestId !== quickAddModalRequestGeneration) return;
      this.#showModalError();
      clearQuickAddTriggerBusy();
      clearQuickAddModalFetchUrl(productPageUrl);
      return;
    }

    if (requestId !== quickAddModalRequestGeneration) return;

    if (!html) {
      clearQuickAddModalFetchUrl(productPageUrl);
      return;
    }

    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) {
      if (requestId !== quickAddModalRequestGeneration) return;
      this.#showModalError();
      clearQuickAddTriggerBusy();
      clearQuickAddModalFetchUrl(productPageUrl);
      return;
    }

    productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    this.#cachedContent.set(cacheKey, productGrid);

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    if (requestId !== quickAddModalRequestGeneration) return;

    if (dialogWasOpen) {
      this.#resetScroll();
    }

    clearQuickAddTriggerBusy();
    clearQuickAddModalFetchUrl(productPageUrl);
  };

  #showModalLoading() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const template = document.getElementById('quick-add-modal-loading-template');
    if (!modalContent || !template) return;
    modalContent.replaceChildren(template.content.cloneNode(true));
  }

  #showModalError() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const template = document.getElementById('quick-add-modal-error-template');
    if (!modalContent || !template) return;

    const fragment = template.content.cloneNode(true);
    const messageEl = fragment.querySelector('[data-quick-add-error-message]');
    if (messageEl) {
      messageEl.textContent = window.Theme?.translations?.recipient_form_error ?? '';
    }

    const retryBtn = fragment.querySelector('[data-quick-add-retry]');
    if (retryBtn instanceof HTMLButtonElement) {
      retryBtn.textContent = window.Theme?.translations?.reset ?? 'Reset';
      retryBtn.addEventListener('click', () => this.retryQuickAdd(), { once: true });
    }

    modalContent.replaceChildren(fragment);
  }

  #focusModalLoadingRegion() {
    requestAnimationFrame(() => {
      const root = document.querySelector('#quick-add-modal-content .quick-add-modal__loading-root');
      if (root instanceof HTMLElement) {
        root.setAttribute('tabindex', '-1');
        root.focus({ preventScroll: true });
      }
    });
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /** @param {QuickAddDialog | null} [dialogComponent] */
  #openQuickAddModal = (dialogComponent = null) => {
    const dc =
      dialogComponent ?? /** @type {QuickAddDialog | null} */ (document.getElementById('quick-add-dialog'));
    if (!(dc instanceof QuickAddDialog)) return;

    dc.showDialog();

    const dialog = dc.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Prefetch full product HTML (hover). Shares in-flight fetch by URL.
   * @param {string} productPageUrl - Product page URL
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    if (this.#hoverPrefetchUrl && this.#hoverPrefetchUrl !== productPageUrl) {
      abortQuickAddInflightByUrl(this.#hoverPrefetchUrl);
    }
    this.#hoverPrefetchUrl = productPageUrl;

    let html = null;
    try {
      html = await this.#fetchProductPageDocument(productPageUrl);
    } finally {
      if (this.#hoverPrefetchUrl === productPageUrl) {
        this.#hoverPrefetchUrl = null;
      }
    }

    if (html) {
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (gridElement) {
        this.#cachedContent.set(quickAddCacheKey(productPageUrl), /** @type {Element} */ (gridElement.cloneNode(true)));
      }
    }

    return html;
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPageDocument(productPageUrl) {
    if (!productPageUrl) return null;

    try {
      return await loadQuickAddPageDocument(productPageUrl);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    resetQuickAddLoadingUi();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
